import { BaseSyntheticEvent, FC, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import uuid from 'react-uuid'
import Swal from 'sweetalert2'
import { PageTitle } from '../../../../../_metronic/layout/core'
import { Button } from '../../../../components/Button/Button'
import { Input } from '../../../../components/Input/Input'
import { Loading } from '../../../../components/Loading/Loading'
import { GET, POST, PUT } from '../../../../services/api'
import { i18n } from '../../../../translate/i18n'
import { getPlugintypeIdByCode } from '../../../../functions/plugintype'
import { BreadCrumbs } from '../../../../components/BreadCrumbs/breadCrumbrs'

enum sessionType {
  default = 'default',
  banner = 'banner',
}

interface PublicityHotel {
  id: number
  pluginspaceId: number
  image?: string
  order: number
  startDate: string
  endDate: string
  status: boolean
  created: string
  updated: string
  title: string
  description: string
}

enum typeBannerHotel {
  BANNER = 'banner',
  HIGHLIGHT = 'highlight',
}

const Content: FC = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState<string>('')
  const [order, setOrder] = useState<number>(0)
  const [status, setStatus] = useState<boolean>(true)
  const [type, setType] = useState<sessionType>(sessionType.default)
  const [bannerDestaque, setBannerDestaque] = useState<PublicityHotel[]>([])
  const [selectedBanner, setSelectedBanner] = useState<PublicityHotel[]>([])

  const getCategories = async () => {
    try {
      setLoading(true)
      const res = await GET(`/session-hotel-classification/${id}`)

      if (!res || !res.success) {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('hospitality_content.messages.filter_error_title')),
          text: String(i18n.t('hospitality_content.category.update.update_error')),
        }).then(() => navigate(-1))
        return
      }

      const banners = await loadBannerDestaque()

      const data = res.data[0]

      setName(data.name)
      setOrder(data.order)
      setStatus(data.status)
      setType(data.type || sessionType.default)

      const selectedBanners = banners.filter((banner: PublicityHotel) =>
        data.banners.some((selected: any) => selected.publicityHotelId === banner.id)
      )

      setSelectedBanner(selectedBanners)

      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  async function loadBannerDestaque() {
    try {
      const res = await POST(`/publicity-hotel/filter`, {
        type: typeBannerHotel.HIGHLIGHT
      })

      if (res.success && res.data) {
        const processedData = res.data.map((item: PublicityHotel) => ({
          ...item,
          img: item.image || '',
          date: new Date(item.created).toLocaleString('pt-BR'),
          period: `${new Date(item.startDate).toLocaleDateString('pt-BR')} - ${new Date(item.endDate).toLocaleDateString('pt-BR')}`,
        }))

        setBannerDestaque(processedData)
        return processedData
      } else {
        setBannerDestaque([])
        return []
      }
    } catch (e) {
      setLoading(false)
      console.log(e)
      return []
    }
  }

  function validateForm() {
    if (!name || order < 0 || isNaN(order) || (selectedBanner.length === 0 && type === sessionType.banner)) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_content.category.update.missing_fields_title')),
        text: String(i18n.t('hospitality_content.category.update.missing_fields_text')),
      })
      return false
    }

    return true
  }

  const edit = async () => {
    try {
      if (!validateForm()) {
        return
      }

      setLoading(true)

      const res = await PUT(`/session-hotel-classification/${id}`, {
        name,
        order,
        status,
        type,
        selectedBanner
      })

      setLoading(false)
      if (res.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hospitality_content.category.update.updated_title')),
          text: String(i18n.t('hospitality_content.category.update.updated_text')),
        }).then(() => navigate('/hospitality/content/category'))
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('hospitality_content.messages.filter_error_title')),
          text: String(i18n.t('hospitality_content.category.update.update_error')),
        })
      }
      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_content.messages.filter_error_title')),
        text: e ?? String(i18n.t('hospitality_content.category.update.update_error')),
      })
    }
  }

  useEffect(() => {
    getCategories()
  }, [])
  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>{`${i18n.t(
                  `general.edit`
                )} ${i18n.t(`hospitality_content.category.page_title`)}`}</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t(`general.save_changes_edit`)}
                </span>
              </h3>
            </div>
            <div className='card-body '>
              <div className='row'>
                <div className='col-lg-12'>
                  <div className='row'>
                    <div className='col-lg-12'>
                      <Input
                        inputAttr={{
                          type: 'default',
                        }}
                        placeholder={i18n.t(`smart_order.name`)}
                        label={i18n.t(`smart_order.name`)}
                        value={name}
                        change={(value: string) => setName(value)}
                        required={true}
                      />
                    </div>
                    <div className='col-lg-6'>
                      <Input
                        inputAttr={{
                          type: 'number',
                        }}
                        placeholder={'0'}
                        label={i18n.t(`smart_order.order`)}
                        value={order}
                        change={(value: string) => {
                          setOrder(parseInt(value))
                        }}
                        required={true}
                      />
                    </div>
                    <div className='col-lg-6'>
                      <Input
                        inputAttr={{ type: 'select' }}
                        value={status}
                        change={(val: boolean) => setStatus(val)}
                        label={String(i18n.t('hospitality_content.category.table.headers.status'))}
                        options={[
                          { select: i18n.t(`general.active`), value: true },
                          { select: i18n.t(`general.inactive`), value: false },
                        ]}
                        required={true}
                      />
                    </div>
                    <div className='col-lg-6'>
                      <Input
                        inputAttr={{ type: 'select' }}
                        value={type}
                        change={(val: sessionType) => setType(val)}
                        label={String(i18n.t('imports.type'))}
                        options={sessionType ? Object.keys(sessionType).map((key) => ({
                          select: i18n.t(`hospitality_content.category.type.${key}`),
                          value: sessionType[key as keyof typeof sessionType],
                        })) : []}
                        required={true}
                      />
                    </div>
                    {type === sessionType.banner && (
                      <div className='col-lg-12 mt-5'>
                        <div className='card'>
                          <div className='card-header'>
                            <h3 className='card-title'>
                              {i18n.t('hospitality_content.category.create.select_banners')}
                            </h3>
                          </div>
                          <div className='card-body'>
                            {bannerDestaque.length === 0 ? (
                              <div className='alert alert-warning'>
                                <div className='d-flex align-items-center'>
                                  <i className='fas fa-exclamation-triangle me-3'></i>
                                  <span>Nenhum banner disponível</span>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className='mb-5'>
                                  <span className='badge badge-light-primary'>
                                    {selectedBanner.length} {selectedBanner.length === 1 ? 'selecionado' : 'selecionados'}
                                  </span>
                                </div>

                                <div className='row g-5'>
                                  {bannerDestaque.map((banner) => {
                                    const isSelected = selectedBanner.some(b => b.id === banner.id);
                                    return (
                                         <div key={banner.id} className='col-lg-3 col-md-3'>
                                        <div
                                          className={`card cursor-pointer ${isSelected ? 'border-primary' : ''}`}
                                          onClick={() => {
                                            if (isSelected) {
                                              setSelectedBanner(selectedBanner.filter(b => b.id !== banner.id))
                                            } else {
                                              setSelectedBanner([...selectedBanner, banner])
                                            }
                                          }}
                                        >
                                          <div className='card-body p-5'>
                                            <div className='mb-4'>
                                              <img
                                                src={banner.image}
                                                alt={banner.title}
                                                className='w-100 rounded'
                                                style={{
                                                  height: '180px',
                                                  objectFit: 'cover'
                                                }}
                                              />
                                            </div>

                                            <h5 className='fw-bold mb-2'>{banner.title}</h5>
                                            <p className='text-muted fs-7 mb-4'>{banner.description}</p>

                                            <div className='d-flex justify-content-between align-items-center'>
                                              <span className='badge badge-light'>
                                                Ordem: {banner.order}
                                              </span>
                                              <span className={`badge ${banner.status ? 'badge-success' : 'badge-danger'}`}>
                                                {banner.status ? 'Ativo' : 'Inativo'}
                                              </span>
                                            </div>
                                          </div>

                                          {isSelected && (
                                            <div className='card-footer bg-light-primary'>
                                              <div className='d-flex align-items-center justify-content-center'>
                                                <i className='fas fa-check-circle text-primary me-2'></i>
                                                <span className='text-primary fw-bold'>Selecionado</span>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className='d-flex w-100'>
              <div className='col-6 d-flex justify-content-start'>
                <Button
                  text={i18n.t(`buttons.back`)}
                  color='info'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate(-1)}
                  btnClass='fw-bolder m-9'
                />
              </div>
              <div className='col-6  d-flex justify-content-end'>
                <Button
                  text={i18n.t(`buttons.save`)}
                  color='primary'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => edit()}
                  btnClass='fw-bolder m-9'
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const FeedEditarCategorias: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          { title: 'Dashboard', path: 'dashboard', isActive: true },
          { title: i18n.t(`smart_order.categories`), path: 'category', isActive: true },
          { title: i18n.t(`general.edit`), path: 'category/editar', isActive: true },
        ]}
      >
        {`${i18n.t(`general.edit`)} ${i18n.t(`smart_order.category`)}`}
      </PageTitle>
      <Content />
    </>
  )
}

export { FeedEditarCategorias }
